import type { ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight } from '@/components/pages/icons';
import { Block } from '../block';
import { parseQuoteItemLink, type QuoteItemLink } from './type';

export function QuoteCardSection({ children }: { children: ReactNode }) {
  return (
    <section className="w-full bg-[#e0f2fe]">
      <Block>
        <div className="mx-auto w-full max-w-page px-5 @lg:px-8">{children}</div>
      </Block>
    </section>
  );
}

export function QuoteCardTitle({ title }: { title: string }) {
  return (
    <h2
      className="font-sans font-normal text-[40px] @lg:text-[64px] leading-[1.2] @lg:leading-[1.1] tracking-[-1.2px] @lg:tracking-[-1.92px] text-brand-text-button text-center @lg:text-left mb-8 @lg:mb-14 [&_i]:font-serif [&_i]:italic [&_i]:text-[44px] @lg:[&_i]:text-[70px] [&_i]:leading-[1.1] [&_i]:tracking-[-0.88px] @lg:[&_i]:tracking-[-1.4px] @lg:[&_br]:hidden"
      dangerouslySetInnerHTML={{ __html: title }}
    />
  );
}

export function QuoteCardImage({ image, alt }: { image: string; alt: string }) {
  return (
    <div className="relative aspect-[358/307] @lg:w-[559px] @lg:aspect-[559/505] shrink-0 overflow-hidden rounded-2xl">
      <Image
        src={image}
        alt={alt ?? ''}
        fill
        className="object-cover object-top"
        sizes="(max-width: 1024px) 100vw, 559px"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent from-[32%] to-black/30" />
    </div>
  );
}

export function QuoteCardAuthor({
  attribution,
  link,
}: {
  attribution: string;
  link: QuoteItemLink | null;
}) {
  const validLink = parseQuoteItemLink(link);

  return (
    <p className="font-sans font-normal text-base @lg:text-xl leading-[1.5] @lg:leading-[1.3] text-brand-text-button">
      {attribution}
      {validLink ? (
        <>
          {' '}
          <Link
            href={validLink.href}
            className="inline-flex items-center gap-1 font-normal text-brand-button transition-colors hover:text-brand-button-hover"
          >
            <span className="underline">{validLink.label}</span>
            <ArrowRight className="size-4 shrink-0" />
          </Link>
        </>
      ) : null}
    </p>
  );
}

export function QuoteCardBadges({ badges }: { badges?: string[] }) {
  if (!badges?.length) return null;

  return (
    <div className="flex items-start gap-6 @lg:gap-8 mt-2">
      {badges
        .filter((badge) => badge?.length)
        .map((badge) => (
          // Image is blurring the image, <img> preserves it
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={badge}
            src={badge}
            alt=""
            className="size-[60px] @lg:size-[76px] object-contain"
          />
        ))}
    </div>
  );
}
